import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import SiteLayout from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, Calendar, Clock, User, ArrowLeft, ArrowRight, Share2 } from 'lucide-react';

const InsightPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPost();
  }, [slug]);

  const loadPost = async () => {
    try {
      setLoading(true);
      
      // Try to load from Firestore first
      const postDoc = await getDoc(doc(db, 'cms_blog_posts', slug));
      
      if (postDoc.exists()) {
        const data = postDoc.data();
        setPost({
          id: postDoc.id,
          ...data,
          publishedAt: data.publishedAt?.toDate?.() || new Date()
        });
      }
      // No fallback - if not in CMS, show not found

      // Load related posts
      const relatedQuery = query(
        collection(db, 'cms_blog_posts'),
        where('status', '==', 'published'),
        orderBy('publishedAt', 'desc'),
        limit(3)
      );
      const relatedSnapshot = await getDocs(relatedQuery);
      const related = relatedSnapshot.docs
        .filter(d => d.id !== slug)
        .slice(0, 3)
        .map(d => ({ id: d.id, ...d.data() }));
      setRelatedPosts(related);
    } catch (error) {
      console.error('Error loading post:', error);
      // No fallback - show not found on error
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <SiteLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SiteLayout>
    );
  }

  if (!post) {
    return (
      <SiteLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
            <p className="text-muted-foreground mb-6">The article you're looking for doesn't exist.</p>
            <Link to="/insights">
              <Button>Back to Insights</Button>
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="py-12 bg-muted/30">
        <div className="container-wide max-w-4xl">
          <Link to="/insights" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Insights
          </Link>
          
          <Badge variant="secondary" className="mb-4">{post.category}</Badge>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-muted-foreground">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author || 'ISOcore Team'}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDate(post.publishedAt)}
            </span>
            {post.readTime && (
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {post.featuredImage && (
        <div className="container-wide max-w-4xl -mt-6 mb-12">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto rounded-xl shadow-lg"
          />
        </div>
      )}

      {/* Content */}
      <article className="py-12">
        <div className="container-wide max-w-4xl">
          <div 
            className="prose prose-lg max-w-none prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4 prose-p:text-muted-foreground prose-p:leading-relaxed prose-li:text-muted-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-blockquote:border-primary prose-blockquote:bg-muted/50 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-lg"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>
      </article>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <section className="py-8 border-t border-border">
          <div className="container-wide max-w-4xl">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, idx) => (
                <Badge key={idx} variant="outline">{tag}</Badge>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-primary text-white">
        <div className="container-wide max-w-4xl text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Transform Your Certification Operations?</h2>
          <p className="text-lg opacity-90 mb-8">
            See how ISOcore can help you implement these best practices with our AI-powered platform.
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/demo">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90">
                Book a Demo
              </Button>
            </Link>
            <Link to="/signup">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
                Start Free Trial
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16">
          <div className="container-wide">
            <h2 className="text-2xl font-bold mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map(related => (
                <Link key={related.id} to={`/insights/${related.slug || related.id}`} className="group">
                  <article className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all">
                    {related.featuredImage && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={related.featuredImage}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <Badge variant="secondary" className="mb-2">{related.category}</Badge>
                      <h3 className="font-bold group-hover:text-primary transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </SiteLayout>
  );
};

export default InsightPostPage;
